
import { useMemo, useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ClipboardList,
  ScanLine,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { format, isValid } from "date-fns";

const STATUS_LABEL = {
  present: { color: "bg-green-100 text-green-800", label: "P" },
  absent: { color: "bg-yellow-100 text-yellow-800", label: "A" },
  excused: { color: "bg-orange-100 text-orange-800", label: "E" },
  refusal: { color: "bg-red-100 text-red-800", label: "R" },
  pending: { color: "bg-slate-100 text-slate-800", label: "Pending" },
};

const safeDate = (v) => {
  if (!v) return "-";
  const d = v instanceof Date ? v : new Date(v);
  return isValid(d) ? format(d, "MMM d, yyyy") : "-";
};
const safeTimestamp = (v) => {
  if (!v) return "-";
  const d = v instanceof Date ? v : new Date(v);
  return isValid(d) ? format(d, "MMM d, yyyy h:mm:ss a") : "-";
};

/**
 * AttendanceLogs — read-only audit view of the AttendanceScanLog collection.
 * Admin / AP / Principal only (enforced by sidebar visibility).
 */
export default function AttendanceLogs() {
  const [tab, setTab] = useState("ea");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");

  // Default range: last 30 days
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const defaultFrom = format(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    "yyyy-MM-dd"
  );
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);

  const [sortConfig, setSortConfig] = useState({ key: "timestamp", direction: "desc" });

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["attendanceScanLogs"],
    queryFn: () => base44.entities.AttendanceScanLog.list("-timestamp", 20000),
    staleTime: 5_000,
  });

  const filtered = useMemo(() => {
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    const s = search.trim().toLowerCase();
    return logs.filter((row) => {
      if ((row.roster_type || "").toLowerCase() !== tab) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (methodFilter !== "all" && row.method !== methodFilter) return false;
      if (row.timestamp) {
        const t = new Date(row.timestamp).getTime();
        if (!Number.isNaN(t)) {
          if (fromMs && t < fromMs) return false;
          if (toMs && t > toMs) return false;
        }
      }
      if (s) {
        const hay = `${row.student_name || ""} ${row.student_id || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [logs, tab, search, statusFilter, methodFilter, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av;
      let bv;
      if (sortConfig.key === "timestamp") {
        av = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        bv = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      } else if (sortConfig.key === "student_name") {
        av = (a.student_name || "").toLowerCase();
        bv = (b.student_name || "").toLowerCase();
      } else if (sortConfig.key === "status") {
        av = a.status || "";
        bv = b.status || "";
      } else {
        av = a[sortConfig.key] || "";
        bv = b[sortConfig.key] || "";
      }
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="w-3 h-3" />
    ) : (
      <ArrowDown className="w-3 h-3" />
    );
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setMethodFilter("all");
    setDateFrom(defaultFrom);
    setDateTo(defaultTo);
    setSortConfig({ key: "timestamp", direction: "desc" });
  };

  const renderTable = () => (
    <Card className="border-none shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          {tab === "ea" ? "EA" : "ASD"} Attendance Log ({sorted.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4">
          <div className="md:col-span-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by student name or ID..."
              data-testid="logs-search"
            />
          </div>
          <div>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="logs-date-from"
            />
          </div>
          <div>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="logs-date-to"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="logs-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="present">P — Present</SelectItem>
              <SelectItem value="absent">A — Absent</SelectItem>
              <SelectItem value="excused">E — Excused</SelectItem>
              <SelectItem value="refusal">R — Refusal</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={methodFilter} onValueChange={setMethodFilter}>
            <SelectTrigger data-testid="logs-method-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="barcode_scan">Barcode Scan</SelectItem>
              <SelectItem value="manual_entry">Manual Entry</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end mb-3">
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="logs-reset-filters">
            <Filter className="w-3 h-3 mr-1" /> Reset Filters
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    onClick={() => handleSort("student_name")}
                    className="flex items-center gap-1 hover:text-blue-700"
                    data-testid="logs-sort-student-name"
                  >
                    Student Name
                    <SortIcon column="student_name" />
                  </button>
                </TableHead>
                <TableHead>Student ID</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("status")}
                    className="flex items-center gap-1 hover:text-blue-700"
                    data-testid="logs-sort-status"
                  >
                    Status
                    <SortIcon column="status" />
                  </button>
                </TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("timestamp")}
                    className="flex items-center gap-1 hover:text-blue-700"
                    data-testid="logs-sort-timestamp"
                  >
                    Timestamp
                    <SortIcon column="timestamp" />
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    {isLoading ? "Loading…" : "No attendance events match these filters."}
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((row) => {
                  const badge = STATUS_LABEL[row.status] || STATUS_LABEL.pending;
                  return (
                    <TableRow key={row.id} data-testid={`logs-row-${row.id}`}>
                      <TableCell className="font-medium">{row.student_name || "-"}</TableCell>
                      <TableCell className="font-mono text-xs">{row.student_id || "-"}</TableCell>
                      <TableCell>
                        <Badge className={badge.color}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          {row.method === "barcode_scan" ? (
                            <>
                              <ScanLine className="w-3 h-3 text-blue-600" />
                              Barcode Scan
                            </>
                          ) : row.method === "manual_entry" ? (
                            <>Manual Entry</>
                          ) : (
                            row.method || "-"
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{safeDate(row.timestamp)}</TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                        {safeTimestamp(row.timestamp)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="attendance-logs-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Attendance Logs</h1>
          <p className="text-slate-500 mt-1">
            Read-only audit trail of every attendance mark on both rosters. Defaults to the last 30 days.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-sm">
            <TabsTrigger value="ea" data-testid="logs-tab-ea">EA Attendance</TabsTrigger>
            <TabsTrigger value="asd" data-testid="logs-tab-asd">ASD Attendance</TabsTrigger>
          </TabsList>
          <TabsContent value="ea">{renderTable()}</TabsContent>
          <TabsContent value="asd">{renderTable()}</TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
